// Copyright (c) 2020 embyt GmbH. All rights reserved.
// Author: Roman Morawek <roman.morawek@embyt.com>

import { IAlarmEntry } from "comtypes";
import { AlarmSource, CloudSettings } from "dbtypes";
import { Bus, IParameter } from "parameter";

class Alarm {
  readonly data: IAlarmEntry;
  private readonly parameter: IParameter;
  private readonly cloudSettings: CloudSettings | undefined;

  constructor(data: IAlarmEntry, parameter: IParameter, cloudSettings: CloudSettings | undefined) {
    this.data = data;
    this.parameter = parameter;
    this.cloudSettings = cloudSettings;
  }

  public getText(): string {
    const source = this.data.source;
    const value = this.data.value;
    const parameters = this.parameter;

    if (source === "NVAlarm_Targetvalue") {
      const regulationId = value & 0x1f;
      // find regulation by id or index (fallback)
      const regulation = parameters.regulation.find(
        (curItem, curIndex) => regulationId === (curItem.id ?? curIndex),
      );
      const tl = parameters.tariffline.find(
        (curItem, curIndex) => regulation?.tariffline === (curItem.id ?? curIndex),
      );
      const unit = tl?.unit;
      const targetValue = (value >> 12) & 0x3ff;
      const overshoot = (value >> 22) & 0x7f;
      const time = (value >> 5) & 0x7f;
      return `TL: ${
        tl?.name ?? regulationId
      }, Target: ${targetValue} ${unit}, Overshoot: ${overshoot} %, Time: ${time} %`;
    }
    if (source === AlarmSource.Restart) {
      return this.data.description ?? "";
    }
    if (source === AlarmSource.MissingSync) {
      const tlId = value & 0x1f;
      const tl = parameters.tariffline[tlId];
      return "tariff circuit: " + (tl?.name ?? tlId);
    }
    if (source === AlarmSource.EnergyMeter) {
      const itemValue = value & 0xff;
      const itemName = parameters.energymeter[itemValue]?.name ?? `${itemValue}`;
      return "energy meter: " + itemName;
    }
    if (source === AlarmSource.Modul) {
      const bus = this.getBus((value >> 8) & 0xff);
      const module = value & 0xff;
      const errorBits = (value >> 16) & 0x7f;
      const errorText = this.getModuleErrors(bus, module, errorBits);
      if (this.data.description)
        return `module: ${this.data.description}, error: ${errorText.join(", ")}`;
      return `bus: ${bus}, module: ${module}, error: ${errorText.join(", ")}`;
    }
    if (source === AlarmSource.ManualActive) {
      const bus = this.getBus((value >> 16) & 0xff);
      const module = (value >> 8) & 0xff;
      const output = value & 0xff;
      return `bus: ${bus}, module: ${module}, output: ${output}`;
    }
    if (source === AlarmSource.Linx) {
      const internalErrorCode = (value >> 16) & 0xff;
      const otherErrorCode = value & 0xff;
      const errorText: string[] = [];
      if (otherErrorCode & 0x01) errorText.push("CPU load above 80 %");
      if (otherErrorCode & 0x02) errorText.push("Overvoltage");
      if (otherErrorCode & 0x04) errorText.push("Undervoltage");
      if (internalErrorCode) errorText.push(`Internal Error ${internalErrorCode}`);
      return errorText.join(", ");
    }
    if (source === AlarmSource.Offline) {
      // no further data here
      return "";
    }
    if (source === AlarmSource.TrendMonitor) {
      return this.cloudSettings?.trendMonitor?.[value]?.title ?? `${value}`;
    }
    if (
      [
        AlarmSource.OcppChargerOffline,
        AlarmSource.OcppChargerFaulted,
        AlarmSource.OcppChargingInterrupted,
      ].includes(source)
    ) {
      return this.data.description ?? "";
    }

    return "unknown alarm";
  }

  private getModuleErrors(bus: Bus, module: number, errorCode: number) {
    const errorText: string[] = [];

    if (bus === Bus.LC || bus === Bus.FT || bus === Bus.IP) {
      if (errorCode & 0x01) errorText.push("not detected");
      if (errorCode & 0x02) errorText.push("min firmware");
      if (errorCode & 0x04) errorText.push("offline");
      if (errorCode & 0x08) errorText.push("ID mismatch");
      if (errorCode & 0x10) errorText.push("not LIOB 100");
      if (errorCode & 0x20) errorText.push("not configured");
      if (errorCode & 0x40) errorText.push("module error");
    } else if (bus === Bus.M || bus === Bus.MOD) {
      if (errorCode & 0x01) errorText.push("unreliable");
      if (errorCode & 0x02) errorText.push("out of service");
      if (errorCode & 0x04) errorText.push("communication error"); // was: invalid
      if (errorCode & 0x08) errorText.push("offline");
    } else if (bus === Bus.MODULE) {
      if (errorCode & 0x01) errorText.push("offline");
    }
    return errorText;
  }

  private getBus(bus: number): Bus {
    if (bus === 0) return Bus.LC;
    if (bus === 1) return Bus.FT;
    if (bus === 2) return Bus.MOD;
    if (bus === 3) return Bus.M;
    if (bus === 4) return Bus.IP;
    if (bus === 5) return Bus.LOCAL;
    if (bus === 6) return Bus.VIRTUAL;
    if (bus === 7) return Bus.CHARGER;
    if (bus === 8) return Bus.MODULE;
    return Bus.CHARGER; // fallback
  }
}

export default Alarm;
